import { z } from 'zod';
import { LIMITS } from '../config/timing.js';
/**
 * Die fachlichen Begriffe der Anwendung, einmal typisiert und zur Laufzeit
 * prüfbar. Server, Weboberfläche und Simulator benutzen dieselben.
 *
 * Zeitangaben sind durchgehend Millisekunden seit dem 1. Januar 1970 in UTC —
 * in der Datenbank, in der HTTP-Schnittstelle und im Echtzeitprotokoll. JSON
 * kennt keinen Zeittyp; eine Zahl ist eindeutig, muss nicht zerlegt werden und
 * lässt sich unmittelbar für die weiche Fortschreibung der Bewegung
 * weiterrechnen.
 *
 * Koordinaten heißen immer lat und lon, nie x und y und nie ein Zahlenpaar.
 * Das ist der Verwechslung von Länge und Breite vorgebeugt, die sonst jeden
 * Entwurf einmal erwischt.
 */
export const timestampSchema = z.number().int().nonnegative();
export const idSchema = z.uuid();
export const latSchema = z.number().min(-90).max(90);
export const lonSchema = z.number().min(-180).max(180);
export const latLonSchema = z.object({ lat: latSchema, lon: lonSchema });
// ---------------------------------------------------------------------------
// Aufzählungen
// ---------------------------------------------------------------------------
/** Zustand einer Ausfahrt. `paused` ruht über Nacht, ohne beendet zu sein. */
export const tourStateSchema = z.enum(['planned', 'active', 'paused', 'finished']);
export const participantRoleSchema = z.enum(['leader', 'sweeper', 'participant']);
/** Verbindungszustand eines Teilnehmers, wie er in der Datenbank steht. */
export const connectionStateSchema = z.enum(['connected', 'paused', 'no_signal', 'left']);
/**
 * Zustand eines Fahrzeugs in der Kolonne. Wird laufend berechnet, nicht
 * gespeichert.
 *
 * - `in_contact`   Anschluss: Rückstand unterhalb der Schwelle
 * - `lagging`      Zurückgefallen: über der Schwelle, aber noch nicht lange genug
 * - `lost_contact` Kein Anschluss: über der Schwelle und über die Haltezeit hinaus
 * - `off_route`    Abseits: weiter als der Höchstabstand von der Führungsspur
 * - `standstill`   Steht: Geschwindigkeit nahe null über längere Zeit
 * - `no_signal`    Kein Signal: seit dem Zeitablauf keine Positionsmeldung
 * - `paused`       Pausiert: vom Client als Hintergrund gemeldet
 */
export const convoyStateSchema = z.enum([
    'in_contact',
    'lagging',
    'lost_contact',
    'off_route',
    'standstill',
    'no_signal',
    'paused',
]);
/**
 * Arten von Statusmeldungen.
 *
 * Bewusst ohne Prüfregel in der Datenbank: Diese Liste wächst mit der Praxis,
 * und eine neue Art soll keine Migration erfordern. Geprüft wird hier.
 */
export const statusMessageKindSchema = z.enum([
    // --- Aus der Kolonne ------------------------------------------------------
    /** Alles in Ordnung — die Antwort auf die Nachfrage der Führung. */
    'ok',
    /** Pause nötig. */
    'break',
    /** Muss tanken. */
    'fuel',
    /** Panne, bleibe stehen. */
    'breakdown',
    /** Habe den Anschluss verloren, kenne den Weg nicht. */
    'lost',
    /** Bitte langsamer. */
    'slow_down',
    // --- Von der Führung an die Kolonne ---------------------------------------
    /** Wir halten an. */
    'stop',
    /** Gleich Pause. */
    'break_soon',
    /** Bitte aufschließen. */
    'close_up',
    /** Gefahrenstelle voraus. */
    'hazard',
    /** Alles in Ordnung? — die Nachfrage, auf die „ok" antwortet. */
    'check_in',
]);
/** Herkunft einer Positionsmeldung. */
export const positionSourceSchema = z.enum(['live', 'batch', 'simulated']);
/** Sichtbarkeit der Seite. Grundlage der Vordergrund-Bindung. */
export const pageVisibilitySchema = z.enum(['foreground', 'background']);
// ---------------------------------------------------------------------------
// Positionen
// ---------------------------------------------------------------------------
/** Eine einzelne Messung, wie sie das Gerät liefert. */
export const positionSampleSchema = z.object({
    lat: latSchema,
    lon: lonSchema,
    /** Zeitpunkt der Messung auf dem Gerät. */
    recordedAt: timestampSchema,
    accuracyM: z.number().nonnegative(),
    speedMps: z.number().nonnegative().optional(),
    headingDeg: z.number().min(0).lt(360).optional(),
    altitudeM: z.number().optional(),
});
/** Ein Punkt der Führungsspur, wie er übertragen wird. */
export const trackPointSchema = z.object({
    /** Lückenlos aufsteigend je Ausfahrt. Der Client merkt sich die höchste Nummer. */
    seq: z.number().int().nonnegative(),
    lat: latSchema,
    lon: lonSchema,
    recordedAt: timestampSchema,
    /** Kumulierte Streckenlänge ab Beginn des Abschnitts, in Metern. */
    distanceM: z.number().nonnegative(),
    /**
     * Kumulierte **Fahrzeit** ab Beginn des Abschnitts, in Millisekunden.
     *
     * Nicht die verstrichene Zeit: Steht die Führung, wächst sie nicht. Genau
     * darauf beruht der Rückstand — er ist die Fahrzeit, die die Führung von der
     * Stelle eines Fahrzeugs bis zu ihrer jetzigen gebraucht hat, und eine
     * Mittagspause gehört nicht dazu.
     *
     * Das Gegenstück zu distanceM: dort kumulierte Strecke, hier kumulierte Zeit.
     */
    travelMs: z.number().int().nonnegative(),
});
/** Abschnitt der Führungsspur. Ein neuer Abschnitt beginnt nach einer Pause. */
export const trackSegmentSchema = z.object({
    id: idSchema,
    seq: z.number().int().nonnegative(),
    startedAt: timestampSchema,
    endedAt: timestampSchema.nullable(),
});
// ---------------------------------------------------------------------------
// Teilnehmer und Ausfahrt
// ---------------------------------------------------------------------------
/**
 * Ein Teilnehmer, wie ihn die anderen sehen. Gerätekennung und Sitzungstoken
 * kommen hier bewusst nicht vor.
 */
export const participantSchema = z.object({
    id: idSchema,
    displayName: z.string().min(1).max(LIMITS.displayNameMaxLength),
    /**
     * Das Kennzeichen. Beim Beitritt Pflicht — es ist das Einzige, woran ein
     * Fahrzeug in der Kolonne zweifelsfrei zu erkennen ist: Namen wiederholen
     * sich, und rote 911 gibt es auf einer Oldtimer-Ausfahrt mehrere.
     *
     * Hier trotzdem ohne Mindestlänge: Teilnehmer aus Ausfahrten vor dieser
     * Regel haben keines, und ihre Ausfahrt soll sich weiterhin öffnen lassen.
     * Erzwungen wird die Angabe dort, wo sie entsteht — beim Beitritt.
     */
    licensePlate: z.string().max(LIMITS.licensePlateMaxLength),
    /** Art des Fahrzeugs. Freiwillig. */
    vehicleLabel: z.string().max(LIMITS.vehicleLabelMaxLength).nullable(),
    vehicleYear: z.number().int().min(1850).max(2100).nullable(),
    color: z.string().regex(/^#[0-9a-f]{6}$/),
    role: participantRoleSchema,
    connectionState: connectionStateSchema,
    /** Wer sich unsichtbar schaltet, wird weiterhin gelistet, aber nicht mehr geortet. */
    visible: z.boolean(),
    joinedAt: timestampSchema,
});
export const tourSchema = z.object({
    id: idSchema,
    name: z.string().min(1).max(LIMITS.tourNameMaxLength),
    state: tourStateSchema,
    startsAt: timestampSchema.nullable(),
    /**
     * Der Beitrittscode — auch für die, die schon dabei sind.
     *
     * Er ist unter Teilnehmern kein Geheimnis: Jeder ist mit ihm hereingekommen,
     * und wer über den QR-Code beigetreten ist, hat ihn nie gesehen. Ohne ihn
     * kann niemand aus der Kolonne heraus jemanden nachholen — und genau das
     * kommt vor, wenn unterwegs noch ein Fahrzeug dazustößt.
     *
     * Mit dem Ende der Ausfahrt verfällt er.
     */
    joinCode: z.string().regex(/^[0-9]{6}$/),
});
/**
 * Der aktuelle Stand eines Fahrzeugs, so wie er auf der Karte landet.
 *
 * `delaySeconds` ist der Rückstand in Sekunden — eine Zeit, keine Entfernung.
 * Er ist `null`, solange er nicht bestimmbar ist: wenn noch keine Führungsspur
 * vorliegt oder das Fahrzeug zu weit von ihr entfernt ist.
 */
export const vehicleStateSchema = z.object({
    participantId: idSchema,
    lat: latSchema,
    lon: lonSchema,
    /**
     * Zeitpunkt der zugrunde liegenden Messung — auf der Uhr des **fremden**
     * Geräts.
     *
     * Für Darstellung und Zuordnung des Ortes richtig. **Nicht** geeignet, um das
     * Alter zu rechnen: Geht die fremde Uhr falsch, ist jedes daraus gerechnete
     * Alter falsch. In den Daten liegt eine Messung mit Zeitstempel aus 1995.
     * Dafür gibt es `reportAgeS`.
     */
    recordedAt: timestampSchema,
    /**
     * Wie viele Sekunden her es ist, dass der **Server** eine Position dieses
     * Fahrzeugs angenommen hat. `null`, wenn noch nie eine kam.
     *
     * Ein Alter und kein Zeitpunkt — damit ist es von beiden Uhren unabhängig,
     * der des Geräts wie der des Clients. Wer es fortschreiben will, addiert die
     * Zeit, die seit dem Eingang **dieser** Nachricht auf der eigenen Uhr
     * vergangen ist.
     *
     * **Maßgeblich für „veraltet".** Vorher beobachtete jeder Client selbst, wann
     * sich `recordedAt` änderte. Nach einem Neustart der App wusste er es nicht
     * mehr und nahm den Augenblick des Neustarts — am 03.09.2026 stand deshalb
     * „Position 7 min alt" an einem Fahrzeug, das seit 6 h 48 min schwieg (A-99).
     */
    reportAgeS: z.number().nonnegative().nullable(),
    speedMps: z.number().nonnegative().nullable(),
    headingDeg: z.number().min(0).lt(360).nullable(),
    accuracyM: z.number().nonnegative(),
    convoyState: convoyStateSchema,
    /** Rückstand zum Führungsfahrzeug in Sekunden. */
    delaySeconds: z.number().nullable(),
    /** Position entlang der Führungsspur in Metern, ab Beginn des Abschnitts. */
    distanceAlongM: z.number().nullable(),
    /** Entfernung zur Führungsspur in Metern. */
    offTrackM: z.number().nullable(),
});
export const statusMessageSchema = z.object({
    id: idSchema,
    participantId: idSchema,
    kind: statusMessageKindSchema,
    createdAt: timestampSchema,
    lat: latSchema.nullable(),
    lon: lonSchema.nullable(),
    /**
     * Wer den Empfang zurückgemeldet hat.
     *
     * Nur bei Meldungen der Führung gefüllt — nur dort wird quittiert (siehe
     * STATUS_MESSAGE_RULES). Eine leere Liste heißt: noch niemand. Sie ist der
     * einzige Ort für diese Angabe; ein zusätzliches „quittiert ja/nein" an der
     * Meldung gäbe es zweimal und irgendwann verschieden.
     */
    acknowledgedBy: z.array(idSchema),
});
// ---------------------------------------------------------------------------
// Treffpunkte (Stufe 3)
// ---------------------------------------------------------------------------
/**
 * Die Arten eines Treffpunkts.
 *
 * Als `enum` in `shared` **und** als `CHECK` in der Datenbank: Abschnitt 8 des
 * Leitfadens verlangt das für Wertelisten, die fachlich feststehen. Ob diese
 * feststeht, ist die offene Frage aus `docs/stufe-3.md`, Abschnitt 9 — bis sie
 * entschieden ist, gilt die strengere Auslegung. Eine sechste Art zu ergänzen
 * kostet eine Migration; eine falsche in der Datenbank kostet mehr.
 *
 *   * `start`, `finish` — Anfang und Ende. Höchstens einmal, mit Uhrzeit.
 *   * `rally` — Sammelpunkt: Hier wird gewartet, bis alle da sind.
 *   * `program` — Mittagessen, Besichtigung. Uhrzeit **und** Verweildauer.
 *   * `shaping` — Stützpunkt: formt die Route, niemand hält. **Der Fahrer sieht
 *     ihn nicht**; er gehört dem Planer.
 */
export const waypointKindSchema = z.enum(['start', 'rally', 'program', 'shaping', 'finish']);
/** Arten, die während der Fahrt überhaupt erscheinen. */
export const FAHRBARE_ARTEN = Object.freeze([
    'start',
    'rally',
    'program',
    'finish',
]);
/** Ein Treffpunkt, wie ihn Planung und Fahrt gleichermaßen sehen. */
export const waypointSchema = z.object({
    id: idSchema,
    name: z.string().min(1).max(LIMITS.waypointNameMaxLength),
    kind: waypointKindSchema,
    /** Wo der Wagen steht — ein Parkplatz liegt neben der Straße. */
    lat: latSchema,
    lon: lonSchema,
    /** Reihenfolge entlang der Route. Vergibt der Server, nicht der Planer. */
    seq: z.number().int().nonnegative(),
    /**
     * Wo der Punkt **auf der Route** liegt, in Metern.
     *
     * Die zweite Ortsangabe, und beide werden gebraucht: Ohne sie lässt sich
     * weder „der nächste Treffpunkt" bestimmen noch eine Ankunftszeit rechnen.
     * `null`, solange keine Route geplant ist — eine Null wäre eine Behauptung.
     */
    distanceM: z.number().nonnegative().nullable(),
    /** Umkreis, ab dem eine Ankunft gilt. `null` heißt: Es gilt die Schwelle der Ausfahrt. */
    radiusM: z.number().positive().nullable(),
    plannedArrival: timestampSchema.nullable(),
    plannedStayS: z.number().int().nonnegative().nullable(),
});
//# sourceMappingURL=types.js.map